import io, { Socket } from 'socket.io-client';
import { RawDeviceProfile } from "../Models/RawDeviceProfile";
import { PictureForWebClient } from "../Models/PictureForWebClient";
import { ConfigProvider } from "./ConfigProvider";
import { Filter } from "../Models/Filter";
import { Heartbeat, HeartbeatPayload } from "../Components/HeartbeatPayload";
import { AuthService } from "./AuthService";
import { HandlersCollection } from "@tblabs/truffle";
import { SocketApiResponse } from "../Models/Api/SocketApiResponse";
import { DeviceConfig } from "../Models/DeviceConfig";
import { DeviceSettingsValidationResult } from "./DeviceSettingsValidationResult";
import { DeviceSettingsUpdateRequestCache } from "./DeviceSettingsUpdateRequestCache";
import { DeviceRecord } from '../Models/DeviceRecord';
import { IoState } from "./IoState";


export class Server
{
    public DeviceSettingsUpdateRequestCache = new DeviceSettingsUpdateRequestCache();
    public get Id(): string
    {
        return this._config.CurrentServerId;
    }
    public socket: Socket;
    public OnConnectionChange = new HandlersCollection<(connected: boolean) => void>();
    public OnStatusChange = new HandlersCollection<(text: string) => void>();
    public OnDevicesUpdate = new HandlersCollection<(devices: RawDeviceProfile[]) => void>();
    public OnNewDevice = new HandlersCollection<(device: RawDeviceProfile) => void>();
    public OnDeviceLost = new HandlersCollection<(tid: string) => void>();
    public OnPictureUpdate = new HandlersCollection<(tid: string, pic: PictureForWebClient) => void>();
    public OnHeartbeat = new HandlersCollection<(heartbeat: Heartbeat) => void>();
    public OnProblem = new HandlersCollection<(problem: string, counter: number) => void>();
    public OnEvent = new HandlersCollection<(senderId: string, event: string, eventArgs: any[]) => void>();
    public OnSettingsValidationResult = new HandlersCollection<(tid: string, validationResult: DeviceSettingsValidationResult, config: DeviceConfig) => void>();
    public OnIoStateUpdate = new HandlersCollection<(tid: string, ioState: IoState) => void>();

    private heartBeats = false;
    private heartTimer: NodeJS.Timeout;

    private Log(text: string): void
    {
        console.log('[Server]', text);
        this.OnStatusChange.Call(text);
    }

    constructor(private _config: ConfigProvider, private _auth: AuthService)
    { }

    public Dispose(): void
    {
        clearTimeout(this.heartTimer);
        this.socket?.disconnect();
    }

    public async Request<T>(
        eventName: string,
        eventParams: any[],
        onSuccess: (responseData: T) => void,
        onErrorOrTimeout: (errorMessage: string) => void,
        timeout: number = 5000): Promise<boolean>
    {
        try
        {
            const response = await this.socket
                .timeout(timeout)
                .emitWithAck(eventName, ...eventParams);

            if (!response.IsSuccess)
            {
                onErrorOrTimeout(response.Error);
                return false;
            }

            onSuccess(response.Data);
            return true;
        }
        catch (err: any)
        {
            onErrorOrTimeout(err.message);
            return false;
        }
    }

    public get ConnectedTo(): string
    {
        return this._config.CurrentServerName;
    }

    public GetDevices(filter: Filter): void
    {
        this.Log(`Requesting ${filter} devices list...`);

        this.socket.emit("get-devices", filter, (response: SocketApiResponse<RawDeviceProfile[]>) =>
        {
            if (!response.IsSuccess)
            {
                this.OnProblem.Call("Failed to fetch devices: " + response.Error);
                this.OnDevicesUpdate.Call([]);
                return;
            }

            this.Log(`Fetched ${response.Data.length}${filter == Filter.All ? "" : " " + filter} ${response.Data.length == 1 ? "device" : "devices"}`);
            this.OnDevicesUpdate.Call(response.Data);
        });
    }

    public Logout(): void
    {
        this._auth.Logout();
        this.Dispose();
    }

    public get IsConnected()
    {
        return !!this.socket?.connected && this.heartBeats;
    }

    public UpdateDeviceRecord(id: string, record: DeviceRecord): void
    {
        this.socket.emit("update-record", id, record, (response: SocketApiResponse<void>) =>
        {
            if (!response.IsSuccess)
            {
                this.Problem("Could not update device record: " + response.Error);
            }
        });
    }

    private lastProblem = "";
    private problemsCounter = 0;
    private Problem(problem: string): void
    {
        if (problem != this.lastProblem)
        {
            this.problemsCounter++;
            console.log('🟠 Server problem:', problem);

            this.lastProblem = problem;

            this.OnProblem.Call(problem, this.problemsCounter);
        }
    }

    public Connect(filter: Filter): this
    {
        this.Log(`Connecting to ${this._config.CurrentServerAddr.value} using password "${this._auth.ValidPassword}"...`);

        const handshake = {
            who: "admin",
            password: this._auth.ValidPassword,
            id: this._auth.UserId
        };

        this.socket = io(this._config.CurrentServerAddr.value, { query: handshake });

        this.socket
            .on('connect', () => 
            {
                this.Log(`🟢 Connected over socket again`);
                this.OnConnectionChange.Call(true);
                this.GetDevices(filter);
            })
            .on('disconnect', (reason: string) =>
            {
                this.Log(`🔴 Socket disconnected: ${reason}`);
                this.OnConnectionChange.Call(false);
            })
            .on('connect_error', (error: any) =>
            {
                this.Log(`🔴 Socket error: ${error.message}`);

                this.Problem("No connection to server");
            })
            .on('heartbeat', (heartbeat: HeartbeatPayload) =>
            {
                this.heartBeats = true;
                this.OnHeartbeat.Call(new Heartbeat(heartbeat, this.heartBeats));

                clearTimeout(this.heartTimer);
                this.heartTimer = setTimeout(() =>
                {
                    this.heartBeats = false;
                    this.OnHeartbeat.Call(new Heartbeat(heartbeat, this.heartBeats));
                }, 3000);
            })
            .on('device-profile', (profile: RawDeviceProfile) =>
            {
                this.Log(`${profile.Id} just connected / updated`);
                this.OnNewDevice.Call(profile);
            })
            .on('device-lost', (tid: string) =>
            {
                this.Log(`${tid} just disconnected`);
                this.OnDeviceLost.Call(tid);
            })
            .on('unauthorized', () =>
            {
                this.Log(`Message from server: unauthorized`);
                this.Problem("unauthorized");
            })
            .on('device:settings-validation-result', (tid: string, validationResult: DeviceSettingsValidationResult, config: DeviceConfig) =>
            {
                this.Log(`${tid} settings validation result: ${validationResult.IsValid ? "valid" : "invalid (" + validationResult.Errors.join(", ") + ")"}`);
                this.OnSettingsValidationResult.Call(tid, validationResult, config);
            })
            .on('device:io-state', (tid: string, ioState: IoState) =>
            {
                this.OnIoStateUpdate.Call(tid, ioState);
            })
            .onAny((event: string, senderId: string, ...eventArgs: any[]) =>
            {
                // if (event != "heartbeat")
                // {
                // //     console.log('________________');
                //     console.log('event:', event);
                //     console.log('sender:', senderId);
                //     console.log('args:', ...eventArgs);
                // }

                this.OnEvent.Call(senderId, event, eventArgs);
            })

        return this;
    }
}
